import { Box, Button, Checkbox, Dialog, DialogTitle, Divider, FormControlLabel, Grid2, IconButton, Menu, MenuItem, TextField, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ChangeEvent, ReactElement, useRef, useState } from "react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { LoadingIndicator } from "../../../Common/UI/components/Loading";
import { GridLoadingOverlay } from "../../../Common/UI/components/GridLoadingOverlay";
import { useForm } from "../../../Common/UI/hooks/useForm";
import { useFormError } from "../../../Common/UI/hooks/useFormError";
import { config } from "../../../authConfig";
import useFetchMsal from "../../../Common/UI/hooks/useFetchMsal";
import Vendor from "../interfaces/Vendor";
import LabeledTextField from "./LabeledTextField";
import PersonIcon from "@mui/icons-material/Person";
import RequestQuoteRounded from "@mui/icons-material/RequestQuoteRounded";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { ColDef, SelectionChangedEvent } from "ag-grid-community";
import styles from "../styles/Vendor.module.css";
import RedButton from "../../../Common/UI/components/RedButton";
import { theme } from "../../../Common/UI/styles/theme";

interface VendorSelectionForm {
  vendorName: string;
  taxId: string;
}

interface VendorSelectionFormError {
  taxId: string;
}

export const VendorSelectionDialog = ({ handleClose, handleVendorSelected }: { handleClose: () => void; handleVendorSelected: (vendor: Vendor) => void }): ReactElement => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);
  const execute = useFetchMsal();
  const gridRef = useRef<AgGridReact>(null);
  const [menuAnchorElement, setMenuAnchorElement] = useState<HTMLElement | null>(null);
  const [colsDef, setColsDef] = useState<ColDef[]>([
    { field: "companyName", headerName: "SN Partner Name" },
    { field: "taxNumber", headerName: "Tax ID" },
    { field: "legalName", headerName: "Legal Name" },
    { field: "paymentAddress", headerName: "Payment Address" },
    { field: "legalAddress", headerName: "Legal Address" },
    { field: "entityId", headerName: "ID" },
  ]);

  const handleColumnsMenuChange = (e: ChangeEvent<HTMLInputElement>) => {
    setColsDef((prev) => prev.map((col: ColDef) => (col.field === e.target.name ? { ...col, hide: !col.hide } : col)));
  };

  const { formData, setFormData, handleInputChange } = useForm<VendorSelectionForm>({
    vendorName: "",
    taxId: "",
  });

  const { formErrors, isSubmittable, handleInputValidation } = useFormError<VendorSelectionFormError>({ taxId: "" });

  const normalizeInput = (value: string, previousValue: string) => {
    if (!value) return value;
    const currentValue = value.replace(/[^\d]/g, "");
    const cvLength = currentValue.length;

    if (!previousValue || value.length > previousValue.length) {
      if (cvLength < 3) return currentValue;

      return `${currentValue.slice(0, 2)}-${currentValue.slice(2, 9)}`;
    }
  };

  const handleSearchClick = async () => {
    setIsLoading(true);
    console.log(1);

    try {
      const url = new URL(config.rootAPIUrl + config.rootAPIList!.eventSearch);

      Object.entries(formData)
        .filter(([key, value]) => value)
        .forEach(([key, value]) => {
          url.searchParams.append(key, value);
        });

      const response = await execute("GET", url.href);
      setVendors(response);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const onSelectionChanged = (event: SelectionChangedEvent) => {
    const selectedData = gridRef.current?.api.getSelectedRows();
    if (selectedData && selectedData.length > 0) {
      setSelectedVendor(selectedData[0]);
    }
  };

  const handleVendorSelect = () => {
    if (selectedVendor) {
      handleVendorSelected(selectedVendor);
      handleClose();
    }
  };

  return (
    <Dialog
      open
      aria-hidden={false}
      onClose={handleClose}
      sx={
        vendors.length > 0
          ? {
              "& .MuiDialog-container": {
                "& .MuiPaper-root": {
                  width: "130vw", // Increased width if vendors data persist
                  maxWidth: "130vw",
                  height: "auto",
                },
              },
            }
          : {}
      }
    >
      <Box sx={{ paddingX: "10px", paddingY: "4px" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <DialogTitle sx={{ fontSize: "1.125rem", padding: "0px" }}>Select Vendor</DialogTitle>
          <IconButton
            size="small"
            sx={{ marginRight: "-10px" }}
            onClick={handleClose}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ marginTop: "12px", marginBottom: "12px" }} />

        <Typography variant="subtitle1" style={{ fontWeight: "500" }}>
          Enter Vendor Name, part of name, or Tax ID to search.
        </Typography>
        <Typography variant="subtitle1" style={{ fontWeight: "700" }}>
          If the vendor does not populate, follow the Vendor Setup steps{" "}
          <a
            href="https://successfoods-my.sharepoint.com/:w:/g/personal/allison_kovacik_torchystacos_com/EQRWV-sPnTFFjJdnjV0fpf8BT8mBpgoqU8ZCh3SI-oEUpw?wdOrigin=TEAMS-WEB.p2p_ns.rwc&wdExp=TEAMS-TREATMENT&wdhostclicktime=1740571573863&web=1"
            style={{ textDecoration: "underline", color: theme.palette.primary.main }}
            target="_blank"
            rel="noopener noreferrer"
          >
            HERE
          </a>
        </Typography>

        <Grid2 container spacing={2} sx={{ marginTop: "16px", marginBottom: "16px" }}>
          <Grid2 size={vendors.length > 0 ? "auto" : 4} alignContent={"center"}>
            <LabeledTextField LabelIcon={PersonIcon} label="Vendor Name" />
          </Grid2>
          <Grid2 size={vendors.length > 0 ? 3 : 8}>
            <TextField sx={{ width: "100%" }} label="Vendor Name" name="vendorName" value={formData.vendorName} onChange={handleInputChange} />
          </Grid2>

          <Grid2 size={vendors.length > 0 ? "auto" : 4} alignContent={"center"}>
            <LabeledTextField LabelIcon={RequestQuoteRounded} label="Tax ID" />
          </Grid2>
          <Grid2 size={vendors.length > 0 ? 3 : 8}>
            <TextField
              sx={{ width: "100%" }}
              label="Tax ID"
              name="taxId"
              value={formData.taxId}
              error={!!formErrors.taxId}
              helperText={formErrors.taxId}
              onChange={(e) => {
                setFormData((prev) => ({
                  ...prev,
                  [e.target.name]: normalizeInput(e.target.value, prev.taxId),
                }));
              }}
              onBlur={(e) => {
                handleInputValidation(e, (input) => {
                  const taxIdPattern = /^$|^([0-9]{2}-[0-9]{7})$/;
                  return !taxIdPattern.test(input) ? "Incorrect Tax ID Format! Field Should Be Empty or NN-NNNNNNN" : "";
                });
              }}
            />
          </Grid2>
          <Grid2 size={vendors.length > 0 ? 2 : 12}>
            <Button
              variant="contained"
              disableElevation
              onClick={handleSearchClick}
              disabled={!isSubmittable || isLoading || (formData.taxId === "" && formData.vendorName === "")}
              sx={{ float: vendors.length > 0 ? "none" : "right" }}
            >
              Search
            </Button>
          </Grid2>
        </Grid2>

        <LoadingIndicator show={isLoading} text={"Looking for our Damn Good Vendor..."}>
          {vendors.length > 0 &&
            <Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                {/* <Button
                  variant="contained"
                  startIcon={<ViewWeekIcon />}
                  sx={{
                    alignSelf: "flex-end",
                    mb: 1,
                    mt: 1,
                  }}
                  disableElevation
                  onClick={(e) => setMenuAnchorElement(e.currentTarget)}
                >
                  Columns
                </Button> */}

                <Menu open={Boolean(menuAnchorElement)} anchorEl={menuAnchorElement} onClose={() => setMenuAnchorElement(null)}>
                  {colsDef.map((col: ColDef) => (
                    <MenuItem key={col.field}>
                      <FormControlLabel control={<Checkbox checked={!col.hide} name={col.field} onChange={handleColumnsMenuChange} />} label={col.headerName} />
                    </MenuItem>
                  ))}
                </Menu>
              </Box>
              <div id={styles.eventsGrid} className="ag-theme-quartz">
                <AgGridReact
                  rowSelection={{
                    mode: "singleRow",
                    enableClickSelection: true,
                  }}
                  columnDefs={colsDef}
                  rowData={vendors}
                  loading={isLoading}
                  loadingOverlayComponent={GridLoadingOverlay}
                  ref={gridRef}
                  pagination
                  paginationPageSize={20}
                  defaultColDef={{
                    headerClass: "tableHeader",
                    filter: "agTextColumnFilter",
                    filterParams: {
                      buttons: ["apply", "clear"],
                      closeOnApply: true,
                      maxNumConditions: 4,
                      suppressMovable: true,
                    },
                  }}
                  suppressMovableColumns={true}
                  onGridReady={() => gridRef.current?.api.sizeColumnsToFit()}
                  onSelectionChanged={onSelectionChanged}
                />
              </div>
              <RedButton onClick={handleVendorSelect} disabled={false} text={"Select"} />
            </Box>
          }
        </LoadingIndicator>
      </Box>
    </Dialog>
  );
};
